import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import FormikTextField from '../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../shared/components/formik/FormikSelectField'
import useProjectsResources from '../../../shared/resources/api/v1/admin/docker/useProjectsResources'
import { useTeam } from '../../../contexts/TeamContext'
import { useProject } from '../../../contexts/ProjectContext'
import { useNodes } from '../../../contexts/NodesContext'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'

function ProjectEditPage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { teamSlug, projectSlug } = useParams()
  const { team } = useTeam()
  const { project, loading: projectLoading, error: projectError, reload } = useProject()
  const { update } = useProjectsResources(teamSlug)
  const { nodes, loading: nodesLoading } = useNodes()

  const nodeOptions = useMemo(() => {
    return nodes.map((node) => ({
      value: node.id,
      label: node.name
    }))
  }, [nodes])

  const expectedStateOptions = useMemo(() => {
    return [
      { value: 'running', label: intl.formatMessage({ id: 'dockerProjects.expectedStates.running' }) },
      { value: 'stopped', label: intl.formatMessage({ id: 'dockerProjects.expectedStates.stopped' }) }
    ]
  }, [intl])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    expectedState: Yup.string(),
    nodeIds: Yup.array()
  })

  const initialNodeIds = useMemo(() => {
    return project?.nodeIds || []
  }, [project])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: project?.name || '',
      expectedState: project?.expectedState || 'running',
      nodeIds: initialNodeIds
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const projectData = {
        project: {
          name: values.name,
          expectedState: values.expectedState,
          nodeIds: values.nodeIds
        }
      }

      update(projectSlug, projectData)
        .then(() => {
          return reload()
        })
        .then(() => {
          navigate(`/teams/${team.slug}/docker/projects/${project.slug}`)
        })
        .catch((err) => {
          setStatus(err.message || 'Error updating project')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  if (projectLoading || nodesLoading) {
    return <LoadingState />
  }

  if (projectError) {
    return <ErrorState messageId="dockerProjects.error" />
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team, project }} />
        <Typography variant="h4" component="h1" gutterBottom>
          <FormattedMessage id="dockerProjects.edit.title" />
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          <FormikTextField
            formik={formik}
            name="name"
            label={<FormattedMessage id="dockerProjects.create.nameLabel" />}
            autoFocus
          />

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="expectedState"
              label={<FormattedMessage id="dockerProjects.create.expectedStateLabel" />}
              options={expectedStateOptions}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="nodeIds"
              label={<FormattedMessage id="dockerProjects.create.nodesLabel" />}
              options={nodeOptions}
              multiple
            />
          </Box>

          {formik.status && (
            <Typography color="error" sx={{ mt: 2 }}>
              {formik.status}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting}
            sx={{ mt: 3 }}
          >
            <FormattedMessage id="dockerProjects.edit.submitButton" />
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default ProjectEditPage
